import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BillReceipt from './BillReceipt';

const API_URL = "https://holisticphysiotherapy.in/holistic_bill/api.php"; 

export default function Billing({ user }) {
  const [uhid, setUhid] = useState('');
  const [patient, setPatient] = useState(null);
  const [services, setServices] = useState([]);
  
  const [selectedService, setSelectedService] = useState('');
  const [qty, setQty] = useState(1);
  const [lineDiscountType, setLineDiscountType] = useState('amount');
  const [lineDiscountVal, setLineDiscountVal] = useState(0);

  const [billItems, setBillItems] = useState([]);
  
  const [overallDiscountType, setOverallDiscountType] = useState('amount');
  const [overallDiscountVal, setOverallDiscountVal] = useState(0);

  const [payments, setPayments] = useState({ cash: 0, upi: 0, card: 0 });
  const [createdBillData, setCreatedBillData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}?action=get_services`).then(res => {
      if (res.data && res.data.services) {
        setServices(res.data.services);
      }
    }).catch(err => console.error("Error fetching services:", err));
  }, []);

  // Helper function to reset all billing input fields
  const resetBillingForm = () => {
    setUhid('');
    setPatient(null);
    setSelectedService('');
    setQty(1);
    setLineDiscountType('amount');
    setLineDiscountVal(0);
    setBillItems([]);
    setOverallDiscountType('amount');
    setOverallDiscountVal(0);
    setPayments({ cash: 0, upi: 0, card: 0 });
    setCreatedBillData(null);
  };

  const searchPatient = async () => {
    if (!uhid.trim()) return;
    try {
      const res = await axios.get(`${API_URL}?action=get_patient&uhid=${uhid}`);
      if (res.data.status === 'success') {
        setPatient(res.data.patient);
      } else {
        alert("Patient not found! Please register the patient first.");
      }
    } catch (err) {
      alert("Error searching for patient.");
    }
  };

  const addItem = () => {
    if (!selectedService) {
      alert("Please select a service first.");
      return;
    }

    const s = services.find(serv => 
      String(serv.id) === String(selectedService) || 
      serv.service_name.toLowerCase() === selectedService.toLowerCase()
    );

    if (!s) {
      alert("Selected service not found.");
      return;
    }

    const price = parseFloat(s.price) || 0;
    const quantity = parseInt(qty, 10) || 1;
    const rawTotal = price * quantity;

    let discountAmt = 0;
    const discVal = parseFloat(lineDiscountVal) || 0;

    if (lineDiscountType === 'percent') {
      discountAmt = (rawTotal * discVal) / 100;
    } else {
      discountAmt = discVal;
    }

    discountAmt = Math.min(discountAmt, rawTotal);
    const netTotal = rawTotal - discountAmt;

    setBillItems(prevItems => [
      ...prevItems,
      {
        service_id: s.id,
        service_name: s.service_name,
        price: price,
        qty: quantity,
        discount: discountAmt,
        total: netTotal
      }
    ]);

    // Reset single row inputs
    setSelectedService('');
    setQty(1);
    setLineDiscountVal(0);
  };

  const removeItem = (index) => {
    setBillItems(billItems.filter((_, idx) => idx !== index));
  };

  const calculateSubtotal = () => billItems.reduce((acc, item) => acc + item.total, 0);

  const calculateOverallDiscount = () => {
    const sub = calculateSubtotal();
    const discVal = parseFloat(overallDiscountVal) || 0;
    if (overallDiscountType === 'percent') {
      return (sub * discVal) / 100;
    }
    return discVal;
  };

  const calculateGrandTotal = () => Math.max(0, calculateSubtotal() - calculateOverallDiscount());

  const handleCreateBill = async () => {
    if (billItems.length === 0) {
      alert("Please add at least one service to the bill.");
      return;
    }

    const grandTotal = calculateGrandTotal();
    const paidTotal = (parseFloat(payments.cash) || 0) + (parseFloat(payments.upi) || 0) + (parseFloat(payments.card) || 0);

    if (paidTotal < grandTotal) {
      alert(`Paid amount (₹${paidTotal}) does not match Grand Total (₹${grandTotal})`);
      return;
    }

    let modes = [];
    if (payments.cash > 0) modes.push('Cash');
    if (payments.upi > 0) modes.push('UPI');
    if (payments.card > 0) modes.push('Card');

    const payload = {
      uhid: patient.uhid,
      subtotal: calculateSubtotal(),
      overall_discount: calculateOverallDiscount(),
      total_amount: grandTotal,
      cash_amount: payments.cash,
      upi_amount: payments.upi,
      card_amount: payments.card,
      payment_mode: modes.join(' + '),
      created_by: user?.full_name || user?.username || 'System',
      items: billItems
    };

    try {
      const res = await axios.post(`${API_URL}?action=create_bill`, payload);
      if (res.data.status === 'success') {
        const fullBill = await axios.get(`${API_URL}?action=get_bill_details&bill_no=${res.data.bill_no}`);
        setCreatedBillData(fullBill.data);
      } else {
        alert(res.data.message || "Failed to create bill.");
      }
    } catch (err) {
      alert("Network error while creating bill.");
    }
  };

  // Render receipt view once bill is saved
  if (createdBillData) {
    return (
      <BillReceipt 
        bill={createdBillData.bill} 
        items={createdBillData.items} 
        onBack={resetBillingForm} // Clean state when user clicks 'Back to Billing'
      />
    );
  }

  return (
    <div className="container mt-4">
      <div className="card shadow-sm mb-4">
        <div className="card-header text-white" style={{ backgroundColor: "#007C9D", color: "white" }}>
          <h5 className="mb-0">New Billing</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="input-group">
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Enter UHID..." 
                  value={uhid} 
                  onChange={e => setUhid(e.target.value)} 
                />
                <button className="btn" style={{ backgroundColor: "#007C9D", color: "white" }} onClick={searchPatient}>
                  Fetch Patient
                </button>
              </div>
            </div>
            {patient && (
              <div className="col-md-8">
                <div className="p-2 border rounded bg-light d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{patient.name}</strong> ({patient.gender}, {patient.age} yrs) | Mobile: {patient.phone}
                  </div>
                  <button className="btn btn-sm btn-outline-danger" onClick={resetBillingForm}>
                    Clear Patient
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {patient && (
        <div className="row">
          <div className="col-md-8">
            <div className="card shadow-sm mb-3">
              <div className="card-header bg-secondary text-white">Add Services</div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-md-4">
                    <input
                      list="services-list"
                      className="form-control"
                      placeholder="Type or Select Service..."
                      value={selectedService}
                      onChange={e => setSelectedService(e.target.value)}
                    />
                    <datalist id="services-list">
                      {services.map(s => (
                        <option key={s.id} value={s.service_name}>
                          ₹{s.price}
                        </option>
                      ))}
                    </datalist>
                  </div>
                  <div className="col-md-2">
                    <input type="number" className="form-control" placeholder="Qty" value={qty} onChange={e => setQty(e.target.value)} min="1" />
                  </div>
                  <div className="col-md-2">
                    <select className="form-select" value={lineDiscountType} onChange={e => setLineDiscountType(e.target.value)}>
                      <option value="amount">Disc (₹)</option>
                      <option value="percent">Disc (%)</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <input type="number" className="form-control" placeholder="Disc Val" value={lineDiscountVal} onChange={e => setLineDiscountVal(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <button className="btn btn-success w-100" onClick={addItem}>Add</button>
                  </div>
                </div>
              </div>
            </div>

            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>Service</th>
                  <th>Price</th>
                  <th>Qty</th>
                  <th>Disc</th>
                  <th>Net</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {billItems.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.service_name}</td>
                    <td>₹{item.price}</td>
                    <td>{item.qty}</td>
                    <td>₹{item.discount}</td>
                    <td>₹{item.total}</td>
                    <td>
                      <button className="btn btn-sm btn-danger py-0" onClick={() => removeItem(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {billItems.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">No items added yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="col-md-4">
            <div className="card shadow-sm">
              <div className="card-header bg-dark text-white">Payment & Summary</div>
              <div className="card-body">
                <h6>Subtotal: ₹{calculateSubtotal()}</h6>
                
                <hr />
                <label className="form-label">Overall Bill Discount</label>
                <div className="input-group mb-2">
                  <select className="form-select" value={overallDiscountType} onChange={e => setOverallDiscountType(e.target.value)}>
                    <option value="amount">₹</option>
                    <option value="percent">%</option>
                  </select>
                  <input type="number" className="form-control" value={overallDiscountVal} onChange={e => setOverallDiscountVal(e.target.value)} />
                </div>

                <h5 className="text-danger">Grand Total: ₹{calculateGrandTotal()}</h5>
                <hr />

                <h6>Payment Breakdown:</h6>
                <div className="mb-2">
                  <small>Cash (₹)</small>
                  <input type="number" className="form-control" value={payments.cash} onChange={e => setPayments({...payments, cash: parseFloat(e.target.value)})} />
                </div>
                <div className="mb-2">
                  <small>UPI (₹)</small>
                  <input type="number" className="form-control" value={payments.upi} onChange={e => setPayments({...payments, upi: parseFloat(e.target.value)})} />
                </div>
                <div className="mb-2">
                  <small>Card (₹)</small>
                  <input type="number" className="form-control" value={payments.card} onChange={e => setPayments({...payments, card: parseFloat(e.target.value)})} />
                </div>

                <button className="btn btn-primary w-100 mt-3" style={{ backgroundColor: "#007C9D", color: "white" }} onClick={handleCreateBill}>
                  Generate & Print Bill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}